/**
 * 会话列表侧边栏 — 索引カード / タブ册スタイル
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import FuriganaText from './FuriganaText';
import { splitInterpolatedIdentity, IDENTITY_MARKER } from '../utils/furigana';
import { useFuriganaAria } from '../contexts/FuriganaContext';

function SessionList({ sessions, currentSessionId, onSelectSession, onNewSession, onDeleteSession, myTeacher, joinCode, setJoinCode, joinLoading, joinError, conflictInfo, setConflictInfo, onJoinClass, onLeaveClass, isTeacherMode }) {
  const { t, i18n } = useTranslation();
  const fa = useFuriganaAria();
  // Split embedded date (e.g., "棒グラフ - 2026/3/22") from session title
  const splitTitleDate = (title) => {
    if (!title) return { name: t('sessions.untitled'), date: null };
    const match = title.match(/^(.+?)\s*[-–—]\s*(\d{4}\/\d{1,2}\/\d{1,2}.*)$/);
    if (match) return { name: match[1].trim(), date: match[2].trim() };
    return { name: title, date: null };
  };

  const getGraphTypeEmoji = (graphType) => {
    const emojiMap = {
      'bar_chart': '📊',
      'line_chart': '📈',
      'pie_chart': '🥧',
      'scatter_plot': '🔵',
      'area_chart': '📉',
      'histogram': '🏗',
      'dot_plot': '⚫',
    };
    return emojiMap[graphType] || '📊';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return t('sessions.today');
    if (diffDays === 1) return t('sessions.yesterday');
    if (diffDays < 7) return t('sessions.daysAgo', { count: diffDays });
    return date.toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : 'en-US', { month: 'short', day: 'numeric' });
  };

  // Render an i18n label that interpolates a USERNAME (an unverified proper name) with the NAME bared —
  // never furigana-guessed (小鳥遊 ≠ ことり; Codex R19/R20/R21) — while the fixed chrome keeps its furigana. It
  // interpolates a MARKER for the name so the split follows the TRUE interpolation position and can not collide
  // with chrome that happens to contain the name (Codex R21 #4). This must happen in EVERY UI language: an
  // English label can still interpolate a kanji username, and wrapping that whole label would guess the name.
  const renderIdentityLabel = (i18nKey, valueKey, identity) => {
    if (!identity) return <FuriganaText text={t(i18nKey, { [valueKey]: identity })} />;
    return splitInterpolatedIdentity(t(i18nKey, { [valueKey]: IDENTITY_MARKER }), identity).map((seg, i) => (
      <React.Fragment key={i}>
        {seg.isIdentity ? seg.text : <FuriganaText text={seg.text} />}
      </React.Fragment>
    ));
  };

  return (
    <div
      className="mb-4 flex h-full min-h-0 max-h-full w-full max-w-sm flex-shrink-0 flex-col overflow-hidden rounded-xl xl:mb-0 xl:max-w-none xl:w-[26rem]"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)' }}
    >
      {/* ヘッダー */}
      <div className="relative overflow-hidden px-4 py-3" style={{ background: 'var(--primary-strong)', color: 'var(--text-on-primary)' }}>
        <p className="text-[9px] font-semibold tracking-[0.3em] uppercase opacity-70">{t('sessions.heading')}</p>
        <h2 className="mt-0.5 text-lg font-bold tracking-wide">{t('sessions.heading')}</h2>
        <button
          onClick={onNewSession}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all whitespace-nowrap"
          style={{ background: 'var(--accent)', color: 'var(--text-on-accent)', boxShadow: 'var(--shadow-sm)' }}
        >
          <span className="text-sm">＋</span>
          <span><FuriganaText text={t('sessions.newLesson')} /></span>
        </button>
      </div>

      {/* リスト */}
      <div className="flex-1 overflow-y-auto px-3 py-3 min-h-0" style={{ background: 'var(--bg-base)' }}>
        {sessions.length === 0 ? (
          <div
            className="rounded-lg border-2 border-dashed p-6 text-center"
            style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)', color: 'var(--text-muted)' }}
          >
            <div className="mb-3 text-3xl">📚</div>
            <p className="font-medium" style={{ color: 'var(--text)' }}>{t('sessions.emptyTitle')}</p>
            <p className="mt-2 text-sm leading-relaxed">
              <FuriganaText text={t('sessions.emptyBody')} />
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {sessions.map((session) => {
              const isActive = currentSessionId === session.id;
              return (
                <div
                  key={session.id}
                  className="group rounded-lg p-2 transition-all cursor-pointer"
                  style={{
                    background: isActive ? 'var(--primary-bg)' : 'var(--bg-surface)',
                    border: isActive ? '2px solid var(--primary)' : '1px solid var(--border)',
                    boxShadow: isActive ? '0 0 0 2px var(--primary-bg)' : 'var(--shadow-sm)',
                  }}
                  onClick={() => onSelectSession(session.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {(() => { const { name, date } = splitTitleDate(session.title); return (
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-base flex-shrink-0">{getGraphTypeEmoji(session.graph_type)}</span>
                          <h3 className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>
                            <FuriganaText text={name} />
                          </h3>
                        </div>
                        {date && (
                          <p className="text-[9px] ml-6 mt-0.5 truncate" style={{ color: 'var(--text-muted)' }}>{date}</p>
                        )}
                      </div>
                      ); })()}
                      <div className="mt-1 flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                        <span
                          className="rounded px-1.5 py-0.5 font-medium"
                          style={{ background: 'var(--accent-bg)', color: 'var(--accent-strong)' }}
                        >
                          <FuriganaText text={formatDate(session.updated_at)} />
                        </span>
                        <span>•</span>
                        <span>{t('sessions.messageCount', { count: session.message_count })}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(t('sessions.confirmDelete'))) {
                          onDeleteSession(session.id);
                        }
                      }}
                      className="ml-1 rounded p-1.5 transition-colors opacity-40 hover:opacity-100"
                      style={{ color: 'var(--danger)' }}
                      title={fa(t('sessions.delete'))}
                      aria-label={t('sessions.delete')}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* クラス参加 */}
      {!isTeacherMode && myTeacher !== undefined && (
        <div className="px-3 py-3 text-xs" style={{ borderTop: '2px solid var(--primary)', background: 'var(--primary-bg, rgba(99,102,241,0.06))' }}>
          {myTeacher === null ? (
            <div>
              <p className="font-bold mb-2 text-sm" style={{ color: 'var(--primary)' }}><FuriganaText text={t('sessions.joinClassTitle')} /></p>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={joinCode || ''}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 6))}
                  placeholder={fa(t('sessions.invitePlaceholder'))}
                  aria-label={t('sessions.invitePlaceholder')}
                  maxLength={6}
                  className="flex-1 min-w-0 rounded-md border px-2 py-1.5 text-center text-sm tracking-[0.2em]"
                  style={{
                    fontFamily: "'MFW-UDDigiKyokashoProN-Regular', 'UDデジタル教科書体', sans-serif",
                    borderColor: 'var(--border)',
                    background: 'var(--bg-surface)',
                    color: 'var(--text)',
                    textTransform: 'uppercase',
                  }}
                />
                <button
                  onClick={() => onJoinClass()}
                  disabled={!joinCode || joinCode.length !== 6 || joinLoading}
                  className="rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 flex-shrink-0"
                  style={{ background: 'var(--primary)', color: 'var(--text-on-primary)' }}
                >
                  <FuriganaText text={joinLoading ? '…' : t('sessions.join')} />
                </button>
              </div>
              {joinError && (
                <p className="mt-1.5 text-xs" style={{ color: 'var(--danger)' }}><FuriganaText text={t('sessions.joinError')} /></p>
              )}
              {conflictInfo && (
                <div className="mt-2 rounded-lg border p-2" style={{ borderColor: 'var(--warning)', background: 'var(--warm-bg)' }}>
                  <p className="text-xs mb-1.5" style={{ color: 'var(--text)' }}>
                    {/* conflict message interpolates the current teacher's username → bare the name (Codex R20 #3) */}
                    {renderIdentityLabel('sessions.conflictMessage', 'teacher', conflictInfo.current_teacher)}
                  </p>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => { onJoinClass(conflictInfo.code, true); setConflictInfo(null); }}
                      className="rounded-md px-2 py-1 text-xs font-semibold"
                      style={{ background: 'var(--primary)', color: 'var(--text-on-primary)' }}
                    >
                      <FuriganaText text={t('sessions.switch')} />
                    </button>
                    <button
                      onClick={() => setConflictInfo(null)}
                      className="rounded-md border px-2 py-1 text-xs font-semibold"
                      style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold truncate" style={{ color: 'var(--text)' }}><FuriganaText text={t('sessions.myClass')} /></p>
                {/* the teacher's username is an unverified proper name → bare (never-guess, Codex R19/R20); the
                    fixed 先生 chrome around it keeps its furigana via renderIdentityLabel. */}
                <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                  {renderIdentityLabel('sessions.teacherName', 'name', myTeacher.username)}
                </p>
              </div>
              <button
                onClick={onLeaveClass}
                className="rounded-md border px-2 py-1 text-xs font-semibold flex-shrink-0 transition-colors"
                style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
              >
                <FuriganaText text={t('sessions.leave')} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SessionList;
